import { Component } from "react";
import styles from "./bookable-show-booking.module.less";
import ShowBookingItem from "components/block-elements/show-booking-item/show-booking-item";
import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import {
  ECommerceHelper,
  JetshopService,
  extract,
  isArray,
  createDate,
  DateHelper,
  EventAggregator,
  Calendar,
  PrimaryButton,
} from "@parksandresorts/core";
import JetshopProductService from "services/jetshop-product-service";
import SiteConfigurationStore from "components/stores/site-configuration-store";
import ExpandContent from "components/block-elements/expand-content/expand-content";
import { motion } from "framer-motion";

export default class BookableShowBooking extends Component {
  constructor(props) {
    super(props);
    this.title = extract(props, "title");
    this.typeText = extract(props, "typeText");
    this.headline = extract(props, "headline");
    this.introText = extract(props, "introText.introText");
    this.todayText = extract(props, "todayText");
    this.displayEndTimes = extract(props, "displayEndTimes", false);
    this.changeDateText = SiteConfigurationStore.getLabel("CHANGE_DATE");
    this.loaderText = SiteConfigurationStore.getLabel("LOADER_TEXT", "Laddar");
    this.selectDateText = SiteConfigurationStore.getLabel("SELECT_DATE", "Välj dag");
    this.loadingText = SiteConfigurationStore.getLabel("LOADING_EVENTS", "Laddar föreställningar");
    this.pricePerBookingText = extract(props, "pricePerBookingText");
    this.extraInfoText = extract(props, "extraInfoText");
    this.product = extract(props, "product");
    this.pageLinkInternal = extract(props, "pageLink.internalLink.slug");
    this.pageLink = this.pageLinkInternal ? this.pageLinkInternal : extract(props, "mapLink.externalUrl");
    this.pageLinkTitle = extract(props, "pageLink.title");
    this.pageLinkIcon = extract(props, "pageLink.icon");
    this.pageLinkInNewTab = extract(props, "pageLink.openInNewTab");
    this.noAvailaibleShowsText = extract(props, "noAvailaibleShowsText");
    this.showPriceInCalendar = extract(props, "showPriceInCalendar", false);
    this.jetshopConfig = ECommerceHelper.getJetshopConfig();
    this.currencyDisplayName = this.jetshopConfig.currencyDisplayName;
    this.priceAndCurrency = "";
    this.showBookingItems = [];
    this.calendarData = [];
    this.today = createDate();
    this.today.setHours(0, 0, 0, 0);
    this.calendarEndDate = createDate(this.today);
    this.calendarEndDate.setMonth(this.calendarEndDate.getMonth() + 11);

    this._isMounted = true;

    this.showMoreTimeslotsLabel = SiteConfigurationStore.getLabel("SHOW_MORE_TIMESLOTS");
    this.showLessTimeslotsLabel = SiteConfigurationStore.getLabel("SHOW_LESS_TIMESLOTS");
    this.remainingSlotsLimit = extract(props, "remainingSlotsLimit") ? extract(props, "remainingSlotsLimit") : 0;

    this.state = {
      selectedDate: null,
      calendarOpen: true,
      calendarMonth: null,
      allTimeSlotDataLoading: true,
      timeslotsLoading: false,
      isFetchingProductPrice: true,
    };

    this.clickHandlerSingleDate = this.clickHandlerSingleDate.bind(this);
    this.onChangeMonth = this.onChangeMonth.bind(this);
  }

  componentDidMount() {
    this.getPrice()
      .then(() => {
        this.getCalendarTimeslotData();
      })
      .catch((err) => {
        EventAggregator.publish("trackException", {
          exception: err,
          customMessage: "BookableShowBooking getPrice",
        });
      });
  }

  switchToDate(date) {
    this.setState({ selectedDate: [date], timeslotsLoading: true });
    this.getTimeslotsForDate(date);
  }

  sortItemByTime(dateItemA, dateItemB) {
    if (!dateItemA || !dateItemB) {
      return -1;
    }

    if (dateItemA.time instanceof Date && dateItemB.time instanceof Date) {
      return DateHelper.compareDates(dateItemA.time, dateItemB.time);
    }

    return -1;
  }

  getCalendarTimeslotData() {
    const productId = this.product.productData.jetshopId;
    const from = DateHelper.toStringYYYYMMDD(this.today);
    const to = DateHelper.toStringYYYYMMDD(this.calendarEndDate);

    JetshopService.getProductTimeslotsInterval(productId, from, to)
      .then((response) => {
        if (!this._isMounted) {
          return;
        }

        const fetchedCalendarData = [];

        const firstAvailableDate = this.getFirstAvailableDate(response);

        for (const dateString of Object.keys(response.dateTimeSlots)) {
          const calendarDataObj = {
            date: createDate(dateString),
            open: true,
            price: this.showPriceInCalendar ? this.priceAndCurrency : null,
          };
          fetchedCalendarData.push(calendarDataObj);
        }
        this.calendarData = fetchedCalendarData;
        this.setState({ allTimeSlotDataLoading: false, calendarMonth: firstAvailableDate });
      })
      .catch((error) => {
        EventAggregator.publish("trackException", {
          exception: error,
          customMessage: "BookableShowBooking getCalendarTimeslotData",
        });
        this.setState({ allTimeSlotDataLoading: false });
      });
  }

  getFirstAvailableDate(response) {
    if (!(response && response.dateTimeSlots)) {
      return null;
    }

    const dateStrings = Object.keys(response.dateTimeSlots);
    if (!isArray(dateStrings)) {
      return null;
    }

    const date = createDate(dateStrings[0]);
    return date;
  }

  getTimeslotsForDate(date) {
    if (!this.product) {
      return;
    }

    const productId = this.product.productData.jetshopId;
    const dateAsString = DateHelper.toStringYYYYMMDD(date);

    JetshopService.getProductTimeslots(productId, dateAsString)
      .then((response) => {
        if (!this._isMounted) {
          return;
        }

        this.showBookingItems = this.transformTimeslots(response.timeSlots);
        this.showBookingItems.sort(this.sortItemByTime);
        this.setState({ timeslotsLoading: false });
      })
      .catch((error) => {
        EventAggregator.publish("trackException", {
          exception: error,
          customMessage: "BookableShowBooking getTimeslotsForDate",
        });
        this.setState({ timeslotsLoading: false });
      });
  }

  getPrice() {
    return JetshopProductService.getProducts(this.jetshopConfig.culture, this.jetshopConfig.currency)
      .then((data) => {
        if (!this._isMounted || !data.products) {
          return;
        }

        if (!this.product) {
          return;
        }

        const jetshopProduct = data.products.find(
          (jetProd) => this.product.productData && jetProd.productId === this.product.productData.jetshopId,
        );
        if (jetshopProduct) {
          this.priceAndCurrency = jetshopProduct.price + " " + this.currencyDisplayName;
        }

        this.setState({
          isFetchingProductPrice: false,
        });
        return Promise.resolve();
      })
      .catch((err) => {
        if (err.status) {
          console.error(
            this.jetshopConfig.errorMessages.find((message) => message.key === err.status.toString()).value,
            err.error,
          );
        } else {
          EventAggregator.publish("trackException", {
            exception: err,
            customMessage: "BookableShowBooking getPrice",
          });
        }
        this.setState({
          isFetchingProductPrice: false,
        });
        return Promise.reject();
      });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  transformTimeslots(timeslots) {
    if (!(timeslots && timeslots.length)) {
      return [];
    }

    return timeslots.map((item, index) => {
      const isAllDayActivity = item.timeSlotBegin === item.timeSlotEnd;
      const timeItem = {
        id: index,
        time: createDate(item.timeSlotBegin),
        timeEnd: createDate(item.timeSlotEnd),
        isAllDayActivity: isAllDayActivity,
        quantity: item.quantity,
        remainingSlotsLimit: this.remainingSlotsLimit,
        timeslotId: item.id,
        productName: this.product.productName,
        productData: this.product.productData,
      };

      return timeItem;
    });
  }

  onChangeMonth(month) {
    this.setState({
      calendarMonth: month,
    });
  }

  clickHandlerSingleDate(clickedDate) {
    if (clickedDate.date) {
      this.switchToDate(clickedDate.date);
      this.setState({ calendarOpen: false });
    }
  }

  renderDatePicker() {
    return (
      <motion.div
        initial={{ height: this.state.calendarOpen ? "auto" : 0, opacity: this.state.calendarOpen ? 1 : 0 }}
        animate={{ height: this.state.calendarOpen ? "auto" : 0, opacity: this.state.calendarOpen ? 1 : 0 }}
        transition={{
          height: { duration: 0.3 },
          opacity: { duration: this.state.calendarOpen ? 0.3 : 0.1, delay: this.state.calendarOpen ? 0.2 : 0 },
        }}
        className={styles.calendarWrapper}
      >
        <Calendar
          startDate={this.today}
          changeDate={this.clickHandlerSingleDate}
          selectedDate={this.state.selectedDate}
          changedMonth={this.onChangeMonth}
          currentDisplayedMonth={this.state.calendarMonth ?? this.today}
          calendarData={this.calendarData}
          disableEmptyDates={true}
        />
      </motion.div>
    );
  }

  renderItemsWithReadMore(items) {
    if (items.length <= 4) {
      return items.map((item) => (
        <li key={item.id} className={styles.listItem}>
          <ShowBookingItem displayEndTime={this.displayEndTimes} {...item} />
        </li>
      ));
    }

    const firstFourItems = items.slice(0, 4);
    const remainingItems = items.slice(4);

    return (
      <>
        {firstFourItems.map((item) => (
          <li key={item.id} className={styles.listItem}>
            <ShowBookingItem displayEndTime={this.displayEndTimes} {...item} />
          </li>
        ))}
        <ExpandContent
          extraStyles={styles.expandBlock}
          shrinkLabel={this.showLessTimeslotsLabel ? this.showLessTimeslotsLabel : undefined}
          expandLabel={this.showMoreTimeslotsLabel ? this.showMoreTimeslotsLabel : undefined}
        >
          {remainingItems.map((item) => (
            <li key={item.id} className={styles.listItem}>
              <ShowBookingItem displayEndTime={this.displayEndTimes} {...item} />
            </li>
          ))}
        </ExpandContent>
      </>
    );
  }

  renderTimeslots() {
    if (this.state.timeslotsLoading) {
      return (
        <div className={styles.listWrapper}>
          <p className={["p_small", styles.noShows].join(" ")}>
            {this.loadingText} <span className="icon-ic_spinner spinner-is-loading" />
          </p>
        </div>
      );
    } else {
      return (
        this.showBookingItems &&
        this.showBookingItems.length > 0 && (
          <div className={styles.listWrapper}>
            <ul className={styles.list}>{this.renderItemsWithReadMore(this.showBookingItems)}</ul>
          </div>
        )
      );
    }
  }

  render() {
    const selectedIsToday =
      isArray(this.state.selectedDate) &&
      this.state.selectedDate[0] &&
      DateHelper.compareDates(
        DateHelper.toStringYYYYMMDD(this.state.selectedDate[0]),
        DateHelper.toStringYYYYMMDD(this.today),
      ) === 0;

    return (
      <div className={styles.bookingBlockWrapper}>
        <div className="">
          {this.typeText && <div className={[styles.typeText, "font-preamble"].join(" ")}>{this.typeText}</div>}
          {this.headline && <h3 className={styles.headline}>{this.headline}</h3>}
          {this.introText && <p className={styles.introText}>{this.introText}</p>}
          {isArray(this.state.selectedDate) ? (
            <h4 className={styles.selectedDate}>{`${selectedIsToday ? this.todayText : ""} ${DateHelper.toStringDDMonth(
              this.state.selectedDate[0],
            )}`}</h4>
          ) : (
            <h4 className={styles.selectedDate}>{this.selectDateText}</h4>
          )}
          <PrimaryButton
            iconLeftNew={this.state.calendarOpen ? "close" : "calendar"}
            onClick={() => this.setState({ calendarOpen: !this.state.calendarOpen })}
            label={this.changeDateText}
            complementary
          />
          <div
            className={[
              styles.calendarSlider,
              this.state.calendarOpen ? styles.calendarSliderOpen : styles.calendarSliderClosed,
            ].join(" ")}
          >
            {this.renderDatePicker()}
          </div>
          {this.renderTimeslots()}
          {this.pricePerBookingText && (
            <h5 className={styles.pricePerBooking}>
              {this.pricePerBookingText} <span className={styles.price}>{this.priceAndCurrency}</span>
            </h5>
          )}
          {this.extraInfoText && <p className={styles.extraInfoText}>{this.extraInfoText}</p>}
          {this.pageLink && (
            <LinkWrapper
              url={this.pageLink}
              target={this.pageLinkInNewTab ? "_blank" : undefined}
              className={["p_small", styles.pageLink].join(" ")}
            >
              <span className={styles.pageLinkTitle}>{this.pageLinkTitle}</span>
              <span
                className={[
                  "p",
                  styles.pageLinkIcon,
                  this.pageLinkIcon ? this.pageLinkIcon : "icon-chevron-right",
                ].join(" ")}
              />
            </LinkWrapper>
          )}
        </div>
      </div>
    );
  }
}
